class VehicleShop extends HTMLElement {
    constructor() {
        super();
        this.vehicleJsonData = null;
        this.uniqueMakeModels = [];
        this.matchingUniqueYears = [];
        this.elements = {};
        this.selectedData = {
            make: '',
            model: '',
            year: '',
            trim: '',
            cabType: ''
        };
        this.initJsonData();
    }

    async initJsonData() {
        try {
            const json = await vehicleUtility.tsvVehicleJSON();
            this.initVehicle(json);
        } catch (error) {
            console.error('Failed to load vehicle data:', error);
        }
    }

    initVehicle(json) {
        this.vehicleJsonData = json;
        this.uniqueMakeModels = this.getUniqueMakeModels();
        this.cacheElements();
        this.bindEvents();

        const limitedSuggestions = this.uniqueMakeModels.slice(0, 5);

        const container = this.querySelector("[data-vehicle-suggestion]");
        const suggestionBox = this.querySelector(`[data-vehicle-suggestion-box]`);

        if (!container || !suggestionBox) return;

        this.showElement(suggestionBox);
        container.innerHTML = '';

        limitedSuggestions.forEach(item => {
            const formattedText = item.replace(/\s?\|\s?/g, ' ');
            
            const li = this.createElement('li', formattedText, {
                'data-vehicle-value': '',
                'data-vehicle-model-value': item,
                class: 'block__vehicle-lookup-input-result-li'
            });

            li.addEventListener('click', (e) => {
                container.querySelectorAll("li").forEach((list) => {
                    list.classList.remove("selected");
                });
                e.target.classList.add("selected");
                let targetMakeModel = e.target.getAttribute("data-vehicle-model-value");
                var makeModelValue = e.target.textContent;

                const [make, model] = targetMakeModel.split('|').map(v => v.trim());
                this.selectedData.make = make;
                this.selectedData.model = model;
                this.matchingUniqueYears = this.getUniqueYears(make, model);

                e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-make-model]').value = makeModelValue;
                e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-submit]').classList.remove("d-none");
            });
            container.appendChild(li);
        });
    }

    getUniqueMakeModels() {
        return [...new Set(Object.values(this.vehicleJsonData)
            .map(item => `${item.make} | ${item.model}`)
            .filter(Boolean))];
    }

    cacheElements() {
        const selectors = {
            makeModelInput: '[data-search-vehicle-make-model]',
            makeModelList: '[data-search-vehicle-make-model-list]',
            makeModelBox: '[data-selected-vehicle-make-model]',
            submitButton: '[data-search-vehicle-submit]',
            yearInput: '[data-search-vehicle-year]',
            yearList: '[data-search-vehicle-year-list]',
            yearBox: '[data-selected-vehicle-year]',
            yearSubmitButton: '[data-search-vehicle-year-submit]',
            trimInput: '[data-search-vehicle-trim]',
            trimList: '[data-search-vehicle-trim-list]',
            trimBox: '[data-selected-vehicle-trim]',
            trimSubmitButton: '[data-search-vehicle-trim-submit]',
            cabTypeInput: '[data-search-vehicle-cabType]',
            cabTypeList: '[data-search-vehicle-cabType-list]',
            cabTypeBox: '[data-selected-vehicle-cabType]',
            cabTypeSubmitButton: '[data-search-vehicle-cabType-submit]',
            trimBothSuggestionBox: '[data-trim-both-suggestion-box]',
            cabTypeBothSuggestionBox: '[data-cabtype-both-suggestion-box]',
            dodgeRamMsg: '[data-dodge-ram-msg]' // Add this line
        };

        Object.entries(selectors).forEach(([key, selector]) => {
            this.elements[key] = this.querySelector(selector);
        });
    }

    bindEvents() {
        if (!this.elements.makeModelInput) return;

        this.bindSearchEvents();
        this.bindStepBackEvents();
        this.bindDodgeRamEvents(); // Add this line
    }

    // Add this new method
    bindDodgeRamEvents() {
        if (this.elements.dodgeRamMsg) {
            this.elements.dodgeRamMsg.addEventListener('click', () => {
                // Hide the submit button
                if (this.elements.submitButton) {
                    this.hideElement(this.elements.submitButton);
                }
                
                // Change input value to "Ram"
                if (this.elements.makeModelInput) {
                    this.elements.makeModelInput.value = 'Ram';
                    
                    // Create a synthetic event and trigger the search handler
                    const syntheticEvent = {
                        target: this.elements.makeModelInput
                    };
                    this.handleSearch(syntheticEvent, 'makeModel');
                }
                
                // Hide the dodge ram message
                this.hideElement(this.elements.dodgeRamMsg);
            });
        }
    }

    bindSearchEvents() {
        const searchConfigs = [
            {
                input: this.elements.makeModelInput,
                list: this.elements.makeModelList,
                box: this.elements.makeModelBox,
                submit: this.elements.submitButton,
                type: 'makeModel',
                minLength: 3,
                handler: (e) => this.handleSearch(e, 'makeModel'),
                selectionHandler: (e) => this.handleSelection(e, 'makeModel'),
                submitHandler: (e) => this.handleSubmit(e, 'makeModel')
            },
            {
                input: this.elements.yearInput,
                list: this.elements.yearList,
                box: this.elements.yearBox,
                submit: this.elements.yearSubmitButton,
                type: 'year',
                minLength: 3,
                handler: (e) => this.handleSearch(e, 'year'),
                selectionHandler: (e) => this.handleSelection(e, 'year'),
                submitHandler: (e) => this.handleSubmit(e, 'year')
            },
            {
                input: this.elements.trimInput,
                list: this.elements.trimList,
                box: this.elements.trimBox,
                submit: this.elements.trimSubmitButton,
                suggestionBox: this.elements.trimBothSuggestionBox,
                type: 'trim',
                minLength: 2,
                handler: (e) => this.handleSearch(e, 'trim'),
                selectionHandler: (e) => this.handleSelection(e, 'trim'),
                submitHandler: (e) => this.handleSubmit(e, 'trim')
            },
            {
                input: this.elements.cabTypeInput,
                list: this.elements.cabTypeList,
                box: this.elements.cabTypeBox,
                submit: this.elements.cabTypeSubmitButton,
                suggestionBox: this.elements.cabTypeBothSuggestionBox,
                type: 'cabType',
                minLength: 2,
                handler: (e) => this.handleSearch(e, 'cabType'),
                selectionHandler: (e) => this.handleSelection(e, 'cabType'),
                submitHandler: (e) => this.handleSubmit(e, 'cabType')
            }
        ];

        searchConfigs.forEach(config => this.bindSearchConfig(config));
    }

    bindSearchConfig(config) {
        if (!config.input) return;

        let debounceTimer;
        config.input.addEventListener('keyup', (e) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => config.handler(e), 150);
        });

        const clickTarget = config.suggestionBox || config.list;
        clickTarget?.addEventListener('click', config.selectionHandler);
        config.submit?.addEventListener('click', config.submitHandler);
    }

    bindStepBackEvents() {
        document.addEventListener('click', (e) => {
            const backButton = e.target.closest('[data-step-back-btn]');
            if (!backButton) return;

            e.preventDefault();
            const backStep = backButton.getAttribute('data-back-step');
            
            if (backStep && backStep !== '0') {
                this.navigateToPreviousStep(backButton, backStep);
            } else {
                backButton.closest('.block__vehicle-lookup-popup').classList.add('d-none');
            }
        });
    }

    navigateToPreviousStep(button, backStep) {
        const currentStep = button.closest('.block__vehicle-lookup-popup-details');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => this.hideElement(step));
            
        const previousStep = parentEle.querySelector(`[current-step="${backStep}"]`);
        if (previousStep) this.showElement(previousStep);
    }

    handleSearch(e, type) {
        const query = this.normalizeQuery(e.target.value);
        const config = this.getSearchConfig(type);
        const inputBox = e.target.closest('.block__vehicle-lookup-input-box');
        
        // Toggle active state
        inputBox?.classList.toggle('active-value', !!query);
        if (this.elements.dodgeRamMsg) {
            this.hideElement(this.elements.dodgeRamMsg);
        }
        
        this.toggleSubmitButton(config.submit, false);
        config.list.innerHTML = '';

        if (!query) {
            this.hideElement(config.box);
            return;
        }

        const minLength = this.getMinLength(type);
        if (query.length >= minLength) {
            const matches = this.getMatches(type, query);
            this.populateResults(config.list, config.box, matches, type);
        } else {
            this.showMinLengthMessage(config.list, config.box, minLength, type);
        }
    }

    getSearchConfig(type) {
        const configs = {
            makeModel: {
                list: this.elements.makeModelList,
                box: this.elements.makeModelBox,
                submit: this.elements.submitButton
            },
            year: {
                list: this.elements.yearList,
                box: this.elements.yearBox,
                submit: this.elements.yearSubmitButton
            },
            trim: {
                list: this.elements.trimList,
                box: this.elements.trimBox,
                submit: this.elements.trimSubmitButton
            },
            cabType: {
                list: this.elements.cabTypeList,
                box: this.elements.cabTypeBox,
                submit: this.elements.cabTypeSubmitButton
            }
        };
        return configs[type];
    }

    getMinLength(type) {
        const lengths = { makeModel: 3, year: 3, trim: 2, cabType: 3 };
        return lengths[type] || 2;
    }

    getMatches(type, query) {
        switch (type) {
            case 'makeModel':
                return this.filterMakeModels(query);
            case 'year':
                // Sort years by newest first (descending order)
                return this.matchingUniqueYears
                    .filter(year => String(year).toLowerCase().includes(query))
                    .sort((a, b) => parseInt(b) - parseInt(a));
            case 'trim':
                return this.getMatchingOptions('trim', {
                    make: this.selectedData.make,
                    model: this.selectedData.model,
                    year: this.selectedData.year
                }).filter(trim => String(trim).toLowerCase().includes(query));
            case 'cabType':
                return this.getMatchingOptions('cab_type', {
                    make: this.selectedData.make,
                    model: this.selectedData.model,
                    year: this.selectedData.year,
                    trim: this.selectedData.trim
                }).filter(cabType => String(cabType).toLowerCase().includes(query));
            default:
                return [];
        }
    }

    handleSelection(e, type) {
        const attributeMap = {
            makeModel: 'data-vehicle-model-value',
            year: 'data-vehicle-year-value',
            trim: 'data-vehicle-trim-value',
            cabType: 'data-vehicle-cabType-value'
        };

        const attribute = attributeMap[type];
        if (!e.target.matches(`[${attribute}]`)) return;

        const inputBox = e.target.closest('.block__vehicle-lookup-input-box');
        inputBox?.classList.remove('active-value');

        const selectedValue = e.target.getAttribute(attribute)?.trim();
        const displayText = e.target.textContent?.trim();
        
        // Allow empty string for trim type when "No trim" is selected
        // Allow empty string for cabType type when "No Cab Type" is selected
        if (!selectedValue && !(type === 'trim' && displayText === 'No trim') && !(type === 'cabType' && displayText === 'No Cab Type')) return;

        // Check if selected value contains "Dodge" for makeModel type
        if (type === 'makeModel' && selectedValue && String(selectedValue).toLowerCase().includes('dodge')) {
            // Show the Dodge RAM message
            if (this.elements.dodgeRamMsg) {
                this.showElement(this.elements.dodgeRamMsg);
            }
            // Don't proceed with normal selection flow
            this.updateSelectedData(type, selectedValue);
            this.updateInputAndUI(e.target, this.getDisplayValue(type, selectedValue), type);

            return;
        } else {
            // Hide Dodge RAM message if it was shown
            if (this.elements.dodgeRamMsg) {
                this.hideElement(this.elements.dodgeRamMsg);
            }

            // For trim type with "No trim", use empty string
            // For cabType type with "No Cab Type", use empty string
            const valueToUse = (type === 'trim' && displayText === 'No trim') ? '' : 
                              (type === 'cabType' && displayText === 'No Cab Type') ? '' : selectedValue;
            const displayValue = (type === 'trim' && displayText === 'No trim') ? 'No trim' : 
                               (type === 'cabType' && displayText === 'No Cab Type') ? 'No Cab Type' : this.getDisplayValue(type, selectedValue);
            
            this.updateSelectedData(type, valueToUse);
            this.updateInputAndUI(e.target, displayValue, type);
        }
    }

    updateSelectedData(type, value) {
        switch (type) {
            case 'makeModel':
                const [make, model] = value.split('|').map(v => v.trim());
                this.selectedData.make = make;
                this.selectedData.model = model;
                this.matchingUniqueYears = this.getUniqueYears(make, model);
                break;
            case 'year':
                this.selectedData.year = value;
                break;
            case 'trim':
                this.selectedData.trim = value;
                break;
            case 'cabType':
                this.selectedData.cabType = value;
                break;
        }
    }

    getDisplayValue(type, value) {
        return type === 'makeModel' 
            ? value.replace(/\s*\|\s*/g, ' ')
            : value;
    }

    handleSubmit(e, type) {
        e.preventDefault();
        
        switch (type) {
            case 'makeModel':
                this.navigateToNextStep(e.target);
                this.updateFinalDisplay('[data-show-final-selected-vehicle]', 
                    `${this.selectedData.make} ${this.selectedData.model}`);
                this.handleMakeModelSubmit(e)
                break;
            case 'year':
                this.handleYearSubmit(e);
                break;
            case 'trim':
                this.handleTrimSubmit(e);
                break;
            case 'cabType':
                this.finalizeVehicleSelection();
                break;
        }
    }

    handleMakeModelSubmit(e){
        
        const { make, model } = this.selectedData;
        const matchingYear = this.getMatchingOptions('year', { make, model }).slice(0, 5);

        if (matchingYear.length > 0) {
            this.navigateToNextStep(e.target);
            this.populateSuggestions('[data-year-suggestion]', matchingYear, 'year');
        }
    }

    handleYearSubmit(e) {
        const { make, model, year } = this.selectedData;
        const matchingTrim = this.getMatchingOptions('trim', { make, model, year }).slice(0, 5);

        // Always navigate to trim step
        this.navigateToNextStep(e.target);
        this.updateFinalDisplay('[data-selected-make-model-year]', `${year} ${make} ${model}`);
        
        // If no trim values available, show "No trim" option
        if (matchingTrim.length > 0) {
            this.populateSuggestions('[data-trim-suggestion]', matchingTrim, 'trim');
        } else {
            // Show "No trim" as the only option
            this.populateSuggestions('[data-trim-suggestion]', ['No trim'], 'trim');
        }
    }

    navigateToStepByIndex(element, stepsToSkip = 1) {
        const currentStep = element.closest('.block__vehicle-lookup-popup-details');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        let targetStep = currentStep;
        let nextStepIndex = currentStep.getAttribute('data-next-step');
        
        // Navigate through the steps to find the target step
        for (let i = 0; i < stepsToSkip && nextStepIndex; i++) {
            const step = parentEle.querySelector(`[current-step="${nextStepIndex}"]`);
            if (step) {
                targetStep = step;
                nextStepIndex = step.getAttribute('data-next-step');
            } else {
                break;
            }
        }
        
        // Show the target step
        if (targetStep && targetStep !== currentStep) {
            parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
                .forEach(step => this.hideElement(step));
            this.showElement(targetStep);
            return true;
        }
        return false;
    }

    navigateToCabTypeStep(element) {
        const currentStep = element.closest('.block__vehicle-lookup-popup-details');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        
        // Directly find the step containing cabType input
        const cabTypeStep = parentEle.querySelector('[data-search-vehicle-cabType]')?.closest('.block__vehicle-lookup-popup-details');
        
        if (cabTypeStep) {
            parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
                .forEach(step => this.hideElement(step));
            this.showElement(cabTypeStep);
            return true;
        }
        return false;
    }

    handleTrimSubmit(e) {
        const { make, model, year, trim } = this.selectedData;
        const matchingCabType = this.getMatchingOptions('cab_type', { make, model, year, trim }).slice(0, 5);

        // Always navigate to cabType step
        this.navigateToNextStep(e.target);
        // Display "No trim" if trim is empty
        const trimDisplay = trim || 'No trim';
        this.updateFinalDisplay('[data-selected-make-model-year-trim]', `${year} ${make} ${model} ${trimDisplay}`);
        
        // If no cab types available, show "No Cab Type" option
        if (matchingCabType.length > 0) {
            this.populateSuggestions('[data-cabType-suggestion]', matchingCabType, 'cabType');
        } else {
            // Show "No Cab Type" as the only option
            this.populateSuggestions('[data-cabType-suggestion]', ['No Cab Type'], 'cabType');
        }
    }

    // Utility methods
    normalizeQuery(value) {
        return value.toLowerCase().replace(/\s+/g, ' ').trim();
    }

    filterMakeModels(query) {
        return this.uniqueMakeModels.filter(item => {
            const normalizedItem = item.toLowerCase()
                .replace(/\s*\|\s*/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            return normalizedItem.includes(query);
        });
    }

    getUniqueYears(make, model) {
        const vehicleArray = Object.values(this.vehicleJsonData);
        const matchingYears = vehicleArray
            .filter(v => v.make === make && v.model === model)
            .map(v => v.year)
            .filter(Boolean);
        
        // Sort years in descending order (newest first)
        return [...new Set(matchingYears)].sort((a, b) => parseInt(b) - parseInt(a));
    }

    getMatchingOptions(field, filters) {
        const vehicleArray = Object.values(this.vehicleJsonData);
        const fieldMap = { trim: 'trim', cab_type: 'cab_type', cabType: 'cab_type' };
        const actualField = fieldMap[field] || field;
        
        const matching = vehicleArray
            .filter(v => 
                Object.entries(filters).every(([key, value]) => {
                    // Skip filter if value is empty string (allow any value for that field)
                    if (value === '' || value === null || value === undefined) {
                        return true;
                    }
                    const vehicleValue = (key === 'cab_type' || key === 'cabType') ? v.cab_type : v[key];
                    return vehicleValue === value;
                })
            )
            .map(v => v[actualField])
            .filter(value => value && value.trim() !== '');
        
        const uniqueOptions = [...new Set(matching)];
        
        // Sort years in descending order if this is for year field
        if (field === 'year') {
            return uniqueOptions.sort((a, b) => parseInt(b) - parseInt(a));
        }
        
        return uniqueOptions;
    }

    populateResults(container, resultBox, matches, type) {
        this.showElement(resultBox);
        
        if (matches.length > 0) {
            const attributeMap = {
                makeModel: { 'data-vehicle-model-value': '', 'data-vehicle-value': '' },
                year: { 'data-year-value': '', 'data-vehicle-year-value': '' },
                trim: { 'data-trim-value': '', 'data-vehicle-trim-value': '' },
                cabType: { 'data-cabType-value': '', 'data-vehicle-cabType-value': '' }
            };

            container.innerHTML = matches.map(match => {
                const baseAttrs = attributeMap[type];
                const attrs = { ...baseAttrs };
                
                // Set specific values based on type
                if (type === 'makeModel') {
                    attrs['data-vehicle-model-value'] = match;
                } else if (type === 'year') {
                    attrs['data-vehicle-year-value'] = match;
                    attrs['data-vehicle-make-model'] = `${this.selectedData.make} | ${this.selectedData.model}`;
                } else {
                    attrs[`data-vehicle-${type}-value`] = match;
                    attrs['data-vehicle-year-value'] = this.selectedData.year;
                    attrs['data-vehicle-make-model'] = `${this.selectedData.make} | ${this.selectedData.model}`;
                    
                    if (type === 'cabType') {
                        attrs['data-vehicle-trim-value'] = this.selectedData.trim;
                    }
                }

                const displayValue = type === 'makeModel' ? match.replace(/\s*\|\s*/g, ' ') : match;
                return this.createListItem(displayValue, attrs);
            }).join('');
        } else {
            container.innerHTML = '<li class="data-vehicle-model-list">No results found</li>';
        }
    }

    showMinLengthMessage(container, box, minLength, type) {
        this.showElement(box);
        const itemType = type === 'makeModel' ? 'vehicle' : 
                        type === 'cabType' ? 'cab type' : type;

        if(type == "year"){
            container.innerHTML = `<li class="data-vehicle-model-list">Please enter at least ${minLength} number${minLength > 1 ? 's' : ''} of the ${itemType}.</li>`;
        }else{
            container.innerHTML = `<li class="data-vehicle-model-list">Please enter at least ${minLength} character${minLength > 1 ? 's' : ''} of the ${itemType}.</li>`;
        }
    }

    populateSuggestions(selector, items, type) {
        const container = this.querySelector(selector);
        const suggestionBox = this.querySelector(`[data-${type}-suggestion-box]`);
        
        if (!container || !suggestionBox) return;
        
        this.showElement(suggestionBox);
        container.innerHTML = '';
        
        // Sort items for year type to show newest first
        const sortedItems = type === 'year' 
            ? [...items].sort((a, b) => parseInt(b) - parseInt(a))
            : items;
        
        sortedItems.forEach(item => {
            // For "No trim" option, set the value to empty string
            // For "No Cab Type" option, set the value to empty string
            const actualValue = (type === 'trim' && item === 'No trim') ? '' : 
                               (type === 'cabType' && item === 'No Cab Type') ? '' : item;
            
            const li = this.createElement('li', item, {
                [`data-${type}-value`]: '',
                'data-vehicle-year-value': this.selectedData.year,
                'data-vehicle-make-model': `${this.selectedData.make} | ${this.selectedData.model}`,
                [`data-vehicle-${type}-value`]: actualValue,
                class: 'block__vehicle-lookup-input-result-li'
            });

            if (type === 'year') {
                li.addEventListener('click', (e) => {
                    container.querySelectorAll("li").forEach((list) => {
                        list.classList.remove("selected");
                    });
                    e.target.classList.add("selected");
                    var YearValue = e.target.textContent;

                    this.selectedData.year = YearValue;

                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-year]').value = YearValue;
                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-year-submit]').classList.remove("d-none");
                });
            }
            
            if (type === 'trim') {
                li.addEventListener('click', (e) => {
                    container.querySelectorAll("li").forEach((list) => {
                        list.classList.remove("selected");
                    });
                    e.target.classList.add("selected");
                    const trimValue = e.target.textContent;
                    const actualTrimValue = trimValue === 'No trim' ? '' : trimValue;

                    this.selectedData.trim = actualTrimValue;

                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-trim]').value = trimValue;
                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-trim-submit]').classList.remove("d-none");
                });
            }
            
            if (type === 'cabType') {
                li.setAttribute('data-vehicle-trim-value', this.selectedData.trim);
                li.addEventListener('click', (e) => {
                    container.querySelectorAll("li").forEach((list) => {
                        list.classList.remove("selected");
                    });
                    e.target.classList.add("selected");
                    const cabTypeValue = e.target.textContent;
                    const actualCabTypeValue = cabTypeValue === 'No Cab Type' ? '' : cabTypeValue;

                    this.selectedData.cabType = actualCabTypeValue;

                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-cabType]').value = cabTypeValue;
                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-cabType-submit]').classList.remove("d-none");
                });
            }
            
            container.appendChild(li);
        });
    }

    updateInputAndUI(targetElement, value, type) {
        const inputMap = {
            makeModel: this.elements.makeModelInput,
            year: this.elements.yearInput,
            trim: this.elements.trimInput,
            cabType: this.elements.cabTypeInput
        };
        
        const submitMap = {
            makeModel: this.elements.submitButton,
            year: this.elements.yearSubmitButton,
            trim: this.elements.trimSubmitButton,
            cabType: this.elements.cabTypeSubmitButton
        };
        
        const boxMap = {
            makeModel: this.elements.makeModelBox,
            year: this.elements.yearBox,
            trim: this.elements.trimBox,
            cabType: this.elements.cabTypeBox
        };

        const input = inputMap[type];
        const submit = submitMap[type];
        const box = boxMap[type];

        if (input) input.value = value;
        if (submit) this.toggleSubmitButton(submit, true);
        if (box) this.hideElement(box);

        // Update selection state
        const container = targetElement.closest('.block__vehicle-lookup-input-box');
        container?.querySelectorAll('li').forEach(li => li.classList.remove('selected'));
        targetElement.classList.add('selected');
    }

    navigateToNextStep(element) {
        const currentStep = element.closest('.block__vehicle-lookup-popup-details');
        const nextStepIndex = currentStep.getAttribute('data-next-step');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => this.hideElement(step));
        
        const nextStep = parentEle.querySelector(`[current-step="${nextStepIndex}"]`);
        if (nextStep) this.showElement(nextStep);
    }

    updateFinalDisplay(selector, content) {
        const element = this.querySelector(selector);
        if (element) element.innerHTML = content;
    }

    finalizeVehicleSelection() {
        document.querySelector(".block__vehicle-lookup-popup:not(.d-none) .block__vehicle-lookup-popup-details:not(.d-none)").querySelector(".block__vehicle-lookup-input-button").classList.add("loading");
        
        const { year, make, model, trim, cabType } = this.selectedData;
        
        let vehicleData;
        if (!this.checkIfVehicleExists(year, make, model, trim, cabType)) {
            vehicleData = this.createVehicleData(year, make, model, trim, cabType);
            this.addVehicleToSelection(vehicleData);
        } else {
            // Vehicle already exists, find it and update it
            vehicleData = window.customerSelectedVehicles?.vehicle?.find(vehicle =>
                vehicle.year === year &&
                vehicle.make === make &&
                vehicle.model === model &&
                vehicle.trim === trim &&
                vehicle.cabType === cabType
            );
            
            if (vehicleData) {
                // Update all defaults and set this one as default
                this.updateVehicleDefaults(vehicleData);
            }
        }

        const partNumbers = this.getPartNumbers(year, make, model, trim, cabType);
        this.redirectToResults(partNumbers);
    }

    checkIfVehicleExists(year, make, model, trim, cabType) {
        return window.customerSelectedVehicles?.vehicle?.some(vehicle =>
            vehicle.year === year &&
            vehicle.make === make &&
            vehicle.model === model &&
            vehicle.trim === trim &&
            vehicle.cabType === cabType
        ) || false;
    }

    createVehicleData(year, make, model, trim, cabType) {
        return {
            id: Math.floor(Math.random() * 4000) + 1000,
            year,
            make,
            model,
            trim,
            cabType,
            default: false
        };
    }

    updateVehicleDefaults(selectedVehicleData) {
        const isCustomer = document.body.classList.contains('customer-loggedin');
        
        // List of all vehicle data localStorage keys
        const vehicleDataKeys = [
            'vehicle_data_side',
            'vehicle_data_sahdt',
            'vehicle_data',
            'turf_data',
            'heavy_equipment_data'
        ];

        // Set all vehicles' default to false in all localStorage keys
        vehicleDataKeys.forEach(key => {
            const storedData = localStorage.getItem(key);
            if (storedData) {
                try {
                    const vehicleArray = JSON.parse(storedData);
                    if (Array.isArray(vehicleArray)) {
                        // Set all vehicles' default to false
                        vehicleArray.forEach(vehicle => {
                            if (vehicle && typeof vehicle === 'object') {
                                vehicle.default = false;
                            }
                        });
                        // Save back to localStorage
                        localStorage.setItem(key, JSON.stringify(vehicleArray));
                    }
                } catch (error) {
                    console.error(`Error processing ${key}:`, error);
                }
            }
        });

        // Update vehicle_data array - set all to false, then set selected one to true
        if (window.customerSelectedVehicles?.vehicle && Array.isArray(window.customerSelectedVehicles.vehicle)) {
            // Set all vehicles in the array to default: false
            window.customerSelectedVehicles.vehicle.forEach(v => {
                if (v && typeof v === 'object') {
                    v.default = false;
                }
            });
            
            // Find and set the selected vehicle's default to true
            const vehicleIndex = window.customerSelectedVehicles.vehicle.findIndex(v =>
                v && v.year === selectedVehicleData.year &&
                v.make === selectedVehicleData.make &&
                v.model === selectedVehicleData.model &&
                v.trim === selectedVehicleData.trim &&
                v.cabType === selectedVehicleData.cabType
            );
            
            if (vehicleIndex !== -1) {
                window.customerSelectedVehicles.vehicle[vehicleIndex].default = true;
                // Update the selectedVehicleData reference to match
                selectedVehicleData.default = true;
            } else {
                // Vehicle not found in array, set it directly
                selectedVehicleData.default = true;
            }
        } else {
            // Set the selected vehicle's default to true
            selectedVehicleData.default = true;
        }

        // Update currentSelectedData and currentSelectedDataLabels with the latest vehicle
        localStorage.setItem('currentSelectedData', JSON.stringify(selectedVehicleData));
        localStorage.setItem('currentSelectedDataLabels', JSON.stringify(selectedVehicleData));
        
        // Save vehicle_data localStorage
        if (isCustomer && window.shopifyFormUtility) {
            localStorage.removeItem('vehicle_data');
        } else {
            localStorage.setItem('vehicle_data', JSON.stringify(window.customerSelectedVehicles.vehicle));
        }
    }

    addVehicleToSelection(vehicleData) {
        const isCustomer = document.body.classList.contains('customer-loggedin');
        
        if (!window.customerSelectedVehicles) {
            window.customerSelectedVehicles = { vehicle: [] };
        }
        if (!window.customerSelectedVehicles.vehicle) {
            window.customerSelectedVehicles.vehicle = [];
        }

        // Add the vehicle to the array first
        window.customerSelectedVehicles.vehicle.push(vehicleData);
        
        // Update all defaults (this will also update currentSelectedData and currentSelectedDataLabels)
        this.updateVehicleDefaults(vehicleData);
    }

    getPartNumbers(year, make, model, trim, cabType) {
        const vehicleArray = Object.values(this.vehicleJsonData);
        const filteredVehicles = vehicleArray.filter(item => {
            const matchesCriteria = [
                String(item.year || '').toLowerCase() === String(year || '').toLowerCase(),
                String(item.make || '').toLowerCase() === String(make || '').toLowerCase(),
                String(item.model || '').toLowerCase() === String(model || '').toLowerCase()
            ];

            if (trim) matchesCriteria.push(String(item.trim || '').toLowerCase() === String(trim || '').toLowerCase());
            if (cabType) matchesCriteria.push(String(item.cab_type || '').toLowerCase() === String(cabType || '').toLowerCase());

            return matchesCriteria.every(Boolean);
        });

        const uniquePartNumbers = [...new Set(
            filteredVehicles
                .map(item => item.part_number)
                .filter(Boolean)
        )];
        
        return uniquePartNumbers.map(p => `sku-${p}`).join(',');
    }

    redirectToResults(partNumbers) {
        const isCustomer = document.body.classList.contains('customer-loggedin');
        
        if (isCustomer && window.shopifyFormUtility) {
            shopifyFormUtility._updateVehicleMeta(window.customerSelectedVehicles.vehicle, partNumbers);
        } else {
            window.location.href = `/collections/all?filter.p.tag=${partNumbers}`;
        }
    }

    // DOM utility methods
    createElement(tag, textContent, attributes = {}) {
        const element = document.createElement(tag);
        if (textContent) element.textContent = textContent;
        
        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'class') {
                element.className = value;
            } else {
                element.setAttribute(key, value);
            }
        });
        
        return element;
    }

    createListItem(content, attributes = {}) {
        const attrs = Object.entries({
            ...attributes,
            class: attributes.class || 'data-vehicle-model-list'
        }).map(([k, v]) => `${k}="${v}"`).join(' ');
        
        return `<li ${attrs}>${content}</li>`;
    }

    showElement(element) {
        element?.classList.remove('d-none');
    }

    hideElement(element) {
        element?.classList.add('d-none');
    }

    toggleSubmitButton(button, show) {
        if (!button) return;
        button.classList.toggle('d-none', !show);
    }
}

customElements.define('vehicle-shop', VehicleShop);